import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { getFile } from 'src/app/shared/hooks/file';
import { getNavigatorShareInterface } from 'src/app/shared/share/utils';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { trackShare, trackShareFailure } from './track';

type OpenTiktokEducationModalResult = {
	waitForResult: () => Promise<{ successfulShare: boolean } | undefined>;
};

type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: Record<string, string>;
	openTiktokEducationModal: (options: { onContinueAction: () => Promise<void> }) => OpenTiktokEducationModalResult;
};

export async function shareTiktok(
	data: { url: string; image: File | string | null },
	{ trackingEvents, trackingData, openTiktokEducationModal }: Options,
	track: TrackingFn,
	utilityContext: UtilityContext,
): Promise<boolean> {
	const { url } = data;

	const image = await toFile(data.image, utilityContext);

	const navigator = getNavigatorShareInterface();

	if (!image || !navigator) {
		trackShare(trackingEvents.cancel, trackingData, track);
		return false;
	}

	const triggerNativeShare = async () => {
		try {
			await copyToClipboard(url, utilityContext.i18n);
			await navigator.share({ files: [image] });
			trackShare(trackingEvents.success, trackingData, track);
		} catch (e) {
			const error = e as Error;
			trackShareFailure(
				trackingEvents.failure,
				{
					...trackingData,
					errorName: error.name,
					errorMessage: error.message,
				},
				track,
			);
		}
	};

	trackShare(trackingEvents.intent, trackingData, track);

	const successfulShare = await (async () => {
		try {
			if (!navigator.canShare || !navigator.canShare({ url, files: [image] })) {
				return false;
			}
			trackShare(trackingEvents.success, trackingData, track);
			const modalResult = await openTiktokEducationModal({ onContinueAction: triggerNativeShare }).waitForResult();

			return !!modalResult?.successfulShare;
		} catch (error) {
			return false;
		}
	})();

	trackShare(successfulShare ? trackingEvents.continue : trackingEvents.cancel, trackingData, track);

	return successfulShare;
}

async function toFile(file: File | string | null, utilityContext: UtilityContext): Promise<File | null> {
	if (file instanceof File) {
		return file;
	}
	if (typeof file === 'string') {
		const res = await getFile(file);
		if (!res) {
			void utilityContext.errorReporter.report(
				new Error(`Failed to get image file from URL for tiktok share: ${file}`),
			);
		}
		return res;
	}
	void utilityContext.errorReporter.report(new Error('Missing image for tiktok share'));
	return null;
}
